import { Suspense } from "react";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import AppLayout from "./layout/AppLayout";
import ProtectedRoute, { AdminRoute } from "./components/auth/ProtectedRoute";
import LeadDemoGate from "./components/auth/LeadDemoGate";
import RBACRoute from "./components/auth/RBACRoute";
import HomeRedirect from "./components/auth/HomeRedirect";
import GlobalAdminRoute from "./components/auth/GlobalAdminRoute";
import EditionAdminRoute from "./components/auth/EditionAdminRoute";
import InboundMessagesRoute from "./components/auth/InboundMessagesRoute";
import StoreLeadRoute from "./components/auth/StoreLeadRoute";
import StoreManagerRoute from "./components/auth/StoreManagerRoute";
import ChildStoreMenuBoundary from "./components/auth/ChildStoreMenuBoundary";
import ExternalEditionGate from "./components/auth/ExternalEditionGate";
import { AuthProvider } from "./context/AuthContext";
import { EditionProvider } from "./context/EditionContext";
import { UiZoomProvider } from "./context/UiZoomContext";
import { ScrollToTop } from "./components/common/ScrollToTop";
import ActivityTracker from "./components/system/ActivityTracker";
import ScreenChatMount from "./components/system/ScreenChatMount";
import { ActionBoundary } from "./components/dev/PerfActionOverlay";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { NewBuildProvider } from "./context/NewBuildContext";
import { ConfirmProvider } from "./components/common/ConfirmDialog";
import { ToastProvider } from "./components/common/Toast";
import QueryCachePersistor from "./components/system/QueryCachePersistor";
import { SidebarProvider } from "./context/SidebarContext";
import { SkeletonPage } from "./components/common/Skeleton";

// 페이지는 라우트 진입 시점에 청크를 받아오도록 lazy 로딩(코드 스플리팅).
// 첫 진입에 전체 앱(수십 페이지)을 통째로 받지 않고, "지금 보는 화면" 청크만 내려받는다.
// 가드(ProtectedRoute·RBACRoute·AppLayout 등)는 항상 필요하므로 정적 import 유지.
const LoginPage = lazyWithRetry(() => import("./pages/AuthPages/LoginPage"));
const SignupPage = lazyWithRetry(() => import("./pages/AuthPages/SignupPage"));
const MobilePublicFormPage = lazyWithRetry(() => import("./pages/Public/MobilePublicFormPage"));
const ApplyPage = lazyWithRetry(() => import("./pages/Public/ApplyPage"));
const PolicyUploadPage = lazyWithRetry(() => import("./pages/Public/PolicyUploadPage"));
const MsgForwarderGuidePage = lazyWithRetry(() => import("./pages/Public/MsgForwarderGuidePage"));
const PaybackConfirmPage = lazyWithRetry(() => import("./pages/Public/PaybackConfirmPage"));
const PaybackApplyPage = lazyWithRetry(() => import("./pages/Public/PaybackApplyPage"));
const PublicQuoteSheetPage = lazyWithRetry(() => import("./pages/Public/PublicQuoteSheetPage"));
const PublicRebateSharePage = lazyWithRetry(() => import("./pages/Public/PublicRebateSharePage"));
const StoreStaffPage = lazyWithRetry(() => import("./pages/Store/StoreStaffPage"));
const StoreChildStoresPage = lazyWithRetry(() => import("./pages/Store/StoreChildStoresPage"));
const StorefrontManagementPage = lazyWithRetry(
  () => import("./pages/Store/StorefrontManagementPage"),
);
const UsedPhoneCompensationPage = lazyWithRetry(
  () => import("./pages/Store/UsedPhoneCompensationPage"),
);
// 커스텀 폼빌더 — 공개 폼 + 관리(목록·편집기·응답)
const CustomFormPage = lazyWithRetry(() => import("./pages/Public/CustomFormPage"));
const FormBuilderListPage = lazyWithRetry(() => import("./pages/FormBuilder/FormBuilderListPage"));
const FormBuilderEditorPage = lazyWithRetry(() => import("./pages/FormBuilder/FormBuilderEditorPage"));
const FormBuilderResponsesPage = lazyWithRetry(() => import("./pages/FormBuilder/FormBuilderResponsesPage"));
const AdminTenantsPage = lazyWithRetry(() => import("./pages/Admin/AdminTenantsPage"));
const AdminUsersPage = lazyWithRetry(() => import("./pages/Admin/AdminUsersPage"));
const CrmImportPage = lazyWithRetry(() => import("./pages/Crm/CrmImportPage"));
const CrmImportReviewPage = lazyWithRetry(() => import("./pages/Crm/CrmImportReviewPage"));
const CallRecordingsPage = lazyWithRetry(() => import("./pages/Crm/CallRecordingsPage"));
// CrmImportReviewPage(매핑 마법사)는 2026-07-05 아카이브 — 새 화면에서는 링크하지 않는다.
// 과거 직접 링크 호환을 위해 라우트만 남기고 uploaded 상태는 분석 대기 안내로 막는다.
const CrmLegacyActivationsPage = lazyWithRetry(() => import("./pages/Crm/CrmLegacyActivationsPage"));
const CrmRestorePage = lazyWithRetry(() => import("./pages/Crm/CrmRestorePage"));
const CrmApplicationTrendsPage = lazyWithRetry(() => import("./pages/Crm/CrmApplicationTrendsPage"));
const AdminCrawlingPage = lazyWithRetry(() => import("./pages/Admin/AdminCrawlingPage"));
const InboundMessagesPage = lazyWithRetry(() => import("./pages/Admin/InboundMessagesPage"));
const ActivityDashboardPage = lazyWithRetry(() => import("./pages/Admin/ActivityDashboardPage"));
const PageInventoryPage = lazyWithRetry(() => import("./pages/Admin/PageInventoryPage"));
const AdminStoreMembersPage = lazyWithRetry(() => import("./pages/Admin/AdminStoreMembersPage"));
const AdminPlanAlarmPage = lazyWithRetry(() => import("./pages/Admin/AdminPlanAlarmPage"));
const AdminExternalFeaturesPage = lazyWithRetry(
  () => import("./pages/Admin/AdminExternalFeaturesPage"),
);
const StoreLeadManagementPage = lazyWithRetry(() => import("./pages/Admin/StoreLeadManagementPage"));
const PhotoReadLabPage = lazyWithRetry(() => import("./pages/Admin/PhotoReadLabPage"));
const CrmCustomerListPage = lazyWithRetry(() => import("./pages/Crm/CrmCustomerListPage"));
const PrivacyDestructionPage = lazyWithRetry(
  () => import("./pages/Privacy/PrivacyDestructionPage"),
);
const CrmProspectsPage = lazyWithRetry(() => import("./pages/Crm/CrmProspectsPage"));
const CrmCustomerDetailPage = lazyWithRetry(() => import("./pages/Crm/CrmCustomerDetailPage"));
const CrmApplicationDetailPage = lazyWithRetry(() => import("./pages/Crm/CrmApplicationDetailPage"));
const InventoryDevicesPage = lazyWithRetry(() => import("./pages/Inventory/InventoryDevicesPage"));
const InventoryUsedDevicesPage = lazyWithRetry(() => import("./pages/Inventory/InventoryUsedDevicesPage"));
const InventoryHoldingsPage = lazyWithRetry(() => import("./pages/Inventory/InventoryHoldingsPage"));
const KakaoCounselPage = lazyWithRetry(() => import("./pages/KakaoCounsel/KakaoCounselPage"));
// 과거 상담 내역 업로드 — 지난 카톡 상담 csv·zip 을 모아 두기만 한다(학습은 나중). shared/counsel-chat-archive.ts
const CounselChatArchivePage = lazyWithRetry(
  () => import("./pages/KakaoCounsel/CounselChatArchivePage"),
);
const CounselOnboardingPage = lazyWithRetry(
  () => import("./pages/KakaoCounsel/CounselOnboardingPage"),
);
// 🗄️ 2026-08-22 — 챗봇을 가르치던 화면 셋(훈련장 옛/새·배운 것)의 lazy import 와 갈림길
// 컴포넌트를 여기서 걷었다. 컴포넌트 파일 자체는 pages/KakaoCounsel 아래 그대로 있다.
// 되살릴 땐 이 자리에 import 셋과 CounselTrainingEntry 를 복원하고 App 라우트를 되돌린다.
const PublicTalkPage = lazyWithRetry(() => import("./pages/PublicTalk/PublicTalkPage"));
const FormTemplateCatalogPage = lazyWithRetry(() => import("./pages/Crm/FormTemplateCatalogPage"));
const FormTemplateDetailPage = lazyWithRetry(() => import("./pages/Crm/FormTemplateDetailPage"));
const DealerTemplatesPage = lazyWithRetry(() => import("./pages/Crm/DealerTemplatesPage"));
const CrmCustomerFormSettingsPage = lazyWithRetry(() => import("./pages/Crm/CrmCustomerFormSettingsPage"));
const CrmSignaturePadPage = lazyWithRetry(() => import("./pages/Crm/CrmSignaturePadPage"));
const CrmPipelineStagesSettingsPage = lazyWithRetry(() => import("./pages/Crm/CrmPipelineStagesSettingsPage"));
const CrmSettlementConfigPage = lazyWithRetry(() => import("./pages/Crm/CrmSettlementConfigPage"));
const CrmDealerAddonsPage = lazyWithRetry(() => import("./pages/Crm/CrmDealerAddonsPage"));
const CrmScheduledMessagesPage = lazyWithRetry(() => import("./pages/Crm/CrmScheduledMessagesPage"));
const CrmPaybackPage = lazyWithRetry(() => import("./pages/Crm/CrmPaybackPage"));
const CrmSettlementsPage = lazyWithRetry(() => import("./pages/Crm/CrmSettlementsPage"));
const CrmActivationsCompletePage = lazyWithRetry(() => import("./pages/Crm/CrmActivationsCompletePage"));
const MessageComposePage = lazyWithRetry(() => import("./pages/Crm/MessageComposePage"));
const MessageTodayPage = lazyWithRetry(() => import("./pages/Crm/MessageTodayPage"));
const MessageCalendarPage = lazyWithRetry(() => import("./pages/Crm/MessageCalendarPage"));
const MessageHistoryPage = lazyWithRetry(() => import("./pages/Crm/MessageHistoryPage"));
const MessageRulesPage = lazyWithRetry(() => import("./pages/Crm/MessageRulesPage"));
const CrmDeletedApplicationsPage = lazyWithRetry(() => import("./pages/Crm/CrmDeletedApplicationsPage"));
const CrmArchivedApplicationsPage = lazyWithRetry(() => import("./pages/Crm/CrmArchivedApplicationsPage"));
const CrmExportPage = lazyWithRetry(() => import("./pages/Crm/CrmExportPage"));
// 업무 자료함 — 매장 공용 파일·링크 보관함 (스크린챗 #87). 권한 그룹 없이 로그인만.
const LibraryPage = lazyWithRetry(() => import("./pages/Library/LibraryPage"));
// 신분증 스캔 — 외부 배포용 앱의 첫 기능(shared/external-edition.ts 목록 참조).
const IdCardScanPage = lazyWithRetry(() => import("./pages/External/IdCardScanPage"));
const RecurringTasksPage = lazyWithRetry(() => import("./pages/Store/RecurringTasksPage"));
const UpdatesPage = lazyWithRetry(() => import("./pages/Updates/UpdatesPage"));
const PolicyPipelinePage = lazyWithRetry(() => import("./pages/Policy/PolicyPipelinePage"));
const PolicyUploadHistoryPage = lazyWithRetry(() => import("./pages/Policy/PolicyUploadHistoryPage"));
const PolicyVendorStatusPage = lazyWithRetry(() => import("./pages/Policy/PolicyVendorStatusPage"));
const PolicyArchivePage = lazyWithRetry(() => import("./pages/Policy/PolicyArchivePage"));
const PolicyQuoteExportsPage = lazyWithRetry(() => import("./pages/Policy/PolicyQuoteExportsPage"));
const PolicyCalendarPage = lazyWithRetry(() => import("./pages/Policy/PolicyCalendarPage"));
const PolicyFaceReviewPage = lazyWithRetry(() => import("./pages/Policy/PolicyFaceReviewPage"));
const PolicyVerbalReviewPage = lazyWithRetry(() => import("./pages/Policy/PolicyVerbalReviewPage"));
const PolicyModelGroupsPage = lazyWithRetry(() => import("./pages/Policy/PolicyModelGroupsPage"));
const PolicyAliasesPage = lazyWithRetry(() => import("./pages/Policy/PolicyAliasesPage"));
const PolicyRegionLearningPage = lazyWithRetry(() => import("./pages/Policy/PolicyRegionLearningPage"));
const PolicyVendorsPage = lazyWithRetry(() => import("./pages/Policy/PolicyVendorsPage"));
const PolicyCatalogPage = lazyWithRetry(() => import("./pages/Policy/PolicyCatalogPage"));
const PolicyRebateLookupPage = lazyWithRetry(() => import("./pages/Policy/PolicyRebateLookupPage"));
const PolicyRebateMatrixPage = lazyWithRetry(() => import("./pages/Policy/PolicyRebateMatrixPage"));
const PolicyRebateMatrixFullPage = lazyWithRetry(
  () => import("./pages/Policy/PolicyRebateMatrixFullPage"),
);
const PolicyRawExcelPopoutPage = lazyWithRetry(
  () => import("./pages/Policy/PolicyRawExcelPopoutPage"),
);
const PolicyVerbalPadPopoutPage = lazyWithRetry(
  () => import("./pages/Policy/PolicyVerbalPadPopoutPage"),
);
const PolicyPlanGroupsPage = lazyWithRetry(() => import("./pages/Policy/PolicyPlanGroupsPage"));
const PolicyPlanGroupMatrixPage = lazyWithRetry(() => import("./pages/Policy/PolicyPlanGroupMatrixPage"));
const PolicyQuoteSheetPage = lazyWithRetry(() => import("./pages/Policy/PolicyQuoteSheetPage"));
const PolicyQuoteSheetBuilderPage = lazyWithRetry(
  () => import("./pages/Policy/PolicyQuoteSheetBuilderPage"),
);
// 업체 전용 시세표 틀 — 사진으로 만든 틀의 목록·확정 검토·보기/편집 (2026-09-23)
const PolicyQuoteTemplatesPage = lazyWithRetry(() => import("./pages/Policy/PolicyQuoteTemplatesPage"));
const PolicyQuoteTemplateNewPage = lazyWithRetry(() => import("./pages/Policy/PolicyQuoteTemplateNewPage"));
const PolicyQuoteTemplatePage = lazyWithRetry(() => import("./pages/Policy/PolicyQuoteTemplatePage"));
const PolicyAnomaliesPage = lazyWithRetry(() => import("./pages/Policy/PolicyAnomaliesPage"));
const PolicyConflictReviewPage = lazyWithRetry(() => import("./pages/Policy/PolicyConflictReviewPage"));
const SubsidyChangesPage = lazyWithRetry(() => import("./pages/Policy/SubsidyChangesPage"));
// 모델 관리 — 표준 모델 사전의 유입 이력·확인 도장·공시/정책 연결 상태(모든 역할)
const ModelRegistryPage = lazyWithRetry(() => import("./pages/Policy/ModelRegistryPage"));
const PolicyAnomalyConsolePage = lazyWithRetry(() => import("./pages/Policy/PolicyAnomalyConsolePage"));
const PolicyDataHealthPage = lazyWithRetry(() => import("./pages/Policy/PolicyDataHealthPage"));

// 페이지 청크를 받아오는 잠깐 동안 보여줄 로딩 표시.
/**
 * 화면(라우트) 코드를 내려받는 동안 본문 자리에 까는 뼈대.
 * 예전엔 한가운데 도는 동그라미 하나였는데, 그러면 메뉴를 옮길 때마다 본문이 통째로 하얗게
 * 비워졌다 채워지는 느낌이 났다. 제목 줄 + 도구 줄 + 표 윤곽을 미리 깔아 두면 어느 화면으로
 * 가든 "지금 그 화면이 그려지는 중"으로 읽힌다(2026-09-20).
 */
function PageLoading() {
  return (
    <div className="min-h-[60vh] p-1">
      <SkeletonPage />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* 내부용/외부용 — 지금 어느 얼굴을 그릴지. 로그인 신분으로 판정하므로 AuthProvider 안쪽,
            사이드바·헤더·라우트 가드가 모두 보므로 그 전부의 바깥이다(shared/external-edition.ts). */}
        <EditionProvider>
        <UiZoomProvider>
        <ToastProvider>
          <ConfirmProvider>
            <QueryCachePersistor />
            <ScrollToTop />
            <ActivityTracker />
            {/* 🧩 스크린챗 — «서비스 계정으로 들어오기» 명단에 든 사람에게만 💬 가 뜬다(그 밖엔 패키지를 싣지도 않는다). docs/ops/screenchat-embed.md */}
            <ScreenChatMount />
            <ActionBoundary />
            {/* 배포 뒤 옛 탭을 새 버전으로 갈아태운다 — 옛 화면 코드가 새 서버 응답을 받아 터지기 전에
                (2026-09-03 구간표 업로드 사고). 보고 있는 화면은 그대로 두고 헤더 「업데이트」 버튼
                (NewBuildHeaderButton)만 세우며, 다른 화면으로 옮기는 순간 저절로 바꾼다. 마지막 그물은 ErrorBoundary.
                Provider 가 헤더 버튼과 상태를 나눠 쓰고 머리 줄 없는 화면에서도 돌아야 하므로 AppLayout 바깥(여기)에 있다. */}
            <NewBuildProvider>
            <ErrorBoundary>
              <Suspense fallback={<PageLoading />}>
              <Routes>
            {/* 공개 라우트 */}
            <Route path="/login" element={<LoginPage />} />
            {/* 판매점 직접 가입(task 531) — 가입하면 곧바로 매장대표로 들어간다. 콘솔엔 «직접 가입»으로 갈려 보인다 */}
            <Route path="/signup" element={<SignupPage />} />
            {/* task 255 (2026-05-24): 모바일 4탭 고객 자가입력 폼.
                FIELD_SECTIONS(32 필드) 고정셋. SimplePublicFormPage 대체. */}
            <Route path="/form/:token" element={<MobilePublicFormPage />} />
            <Route path="/form-simple/:token" element={<MobilePublicFormPage />} />
            {/* 매장 태블릿 서명 패드 — 매장별 고정 비밀 주소로 로그인 없이 연다(#674).
                옛 로그인 주소는 QR 재연결 안내만 보여 주고 더는 인증 화면으로 보내지 않는다. */}
            <Route path="/signature-pad/:token" element={<CrmSignaturePadPage />} />
            <Route path="/crm/signature-pad" element={<CrmSignaturePadPage />} />
            {/* 데모 신청(영업 리드) 공개 랜딩 — 로그인 없이 누구나 여는 모바일 폼 */}
            <Route path="/apply" element={<ApplyPage />} />
            {/* 정책파일 업로드 — 콘솔에서 리드에게 발급한 고유 토큰 링크(로그인 없이 파일 업로드) */}
            <Route path="/policy-upload/:token" element={<PolicyUploadPage />} />
            {/* task 405: 고객 페이백 계좌 확인/입력 — 건별 고유 토큰 링크 */}
            <Route path="/payback/:token" element={<PaybackConfirmPage />} />
            {/* 고객 self-등록 페이백 접수 — 매장 상설 QR(kiosk 토큰) */}
            <Route path="/payback-apply/:token" element={<PaybackApplyPage />} />
            {/* 고객 공개 시세표 — 판매자가 발급한 링크로 누구나 접근 (로그인 없음) */}
            <Route path="/quote/:token" element={<PublicQuoteSheetPage />} />
            {/* 도매 업체용 공개 리베이트 합산표 — 발급 때 업체명을 바꿔 얼린 보기 전용 표 */}
            <Route path="/rebate/:token" element={<PublicRebateSharePage />} />
            {/* 문자 송수신 통합 앱 설치 안내 — 폰매장 직원용 (로그인 없음, 폰에서 열어 다운로드) */}
            <Route path="/msg-forwarder" element={<MsgForwarderGuidePage />} />
            {/* 옛 발송 전용 설치 링크는 통합 앱 안내로 보낸다. */}
            <Route path="/sender" element={<Navigate replace to="/msg-forwarder" />} />
            {/* 커스텀 폼빌더 공개 폼 — 매장이 만든 폼을 토큰 링크로 누구나 열어 응답(로그인 없음) */}
            <Route path="/f/:token" element={<CustomFormPage />} />
            {/* 고객 상담 채팅 — 매장이 카톡 채널에 걸어둔 링크로 들어온 고객이 쓴다(로그인 없음).
                고객이 말하면 뒤에서 AI 초안이 만들어지지만, 매장 담당자가 승인해야 이 화면에 나타난다.
                spec: docs/spec/counsel-chat-spec.md */}
            <Route path="/talk/:token" element={<PublicTalkPage />} />

            {/* 보호 라우트 */}
            <Route element={<ProtectedRoute />}>
              {/* 리드 시연 계정은 여기서 시연 화면 하나로 좁혀진다(그 외 계정엔 무영향).
                  보호 라우트 전체가 이 한 곳을 지나므로 새 화면이 생겨도 자동으로 막힌다. */}
              <Route element={<LeadDemoGate />}>
              {/* 외부 배포용 화면으로 보고 있으면 여기서 "열어 준 기능" 밖으로 못 나간다.
                  정식 화면으로 보고 있으면 아무 일도 하지 않는다(shared/external-edition.ts 가 목록의 진본). */}
              <Route element={<ExternalEditionGate />}>
              {/* LNB 없는 전용 창도 도도매 메뉴 상한을 건너뛰지 못한다. */}
              <Route element={<ChildStoreMenuBoundary />}>
              {/* 서식 편집 전용 창 — 인증·CRM 권한은 그대로 확인하되 AppLayout의 LNB/헤더는 제외한다. */}
              <Route element={<RBACRoute pageGroup="crm" />}>
                <Route
                  path="/crm/form-templates/:id/popout"
                  element={
                    <SidebarProvider>
                      <FormTemplateDetailPage standalone />
                    </SidebarProvider>
                  }
                />
              </Route>
              {/* 원본 엑셀 전용 창 — 다른 모니터에 전체화면으로 두고 표준 표 창과 커서를 맞춰
                  대조한다(티켓 #488). 표만 꽉 차게 두려고 AppLayout(LNB·헤더)을 뺀다. */}
              <Route element={<RBACRoute pageGroup="policy" />}>
                <Route
                  path="/policy/rebate-matrix-full/original"
                  element={<PolicyRawExcelPopoutPage />}
                />
                {/* 구두 조건 판 전용 창 — 판이 표를 가리지 않게 창을 갈랐다(티켓 #489).
                    같은 이유로 AppLayout(LNB·헤더)을 빼고 판만 담는다. */}
                <Route
                  path="/policy/rebate-matrix-full/verbal-pad"
                  element={<PolicyVerbalPadPopoutPage />}
                />
              </Route>
              </Route>
              <Route element={<AppLayout />}>
                {/* 메인 진입 — 계정이 실제로 들어갈 수 있는 첫 페이지로 보낸다(권한에서 계산).
                    예전 고정 /crm/customers 리다이렉트는 CRM 권한 없는 계정에서 무한 루프였다. */}
                <Route index path="/" element={<HomeRedirect />} />
                {/* 업무 자료함 — 매장 살림이라 RBAC 그룹에 안 묶는다(로그인만). 상단 통합검색창이
                    전 화면에 떠 있으므로 그 결과로 오는 이 화면도 누구나 열려야 한다. */}
                <Route path="/library" element={<LibraryPage />} />
                {/* 신분증 스캔 — 외부 배포용 앱에 열어 준 첫 기능(내부용에서도 그대로 열린다).
                    RBAC 그룹에 안 묶는다: 서버 창구가 /api/v2/tools/* (로그인만)이고, 고객·매장
                    데이터를 일절 건드리지 않는 순수 도구라 권한을 나눌 축이 없다. */}
                <Route path="/tools/id-card-scan" element={<IdCardScanPage />} />
                {/* 정기 할 일 — 메뉴·화면·전역 알림 띠 모두 매장대표만 접근한다 (#581). */}
                <Route element={<StoreManagerRoute />}>
                  <Route path="/recurring-tasks" element={<RecurringTasksPage />} />
                </Route>
                {/* 업데이트 소식 — 로그인만 하면 누구나. 배포 직후 알림에서 들어오는 자리라
                    권한 그룹에 묶으면 알림은 떴는데 눌러도 못 들어가는 사람이 생긴다. */}
                <Route path="/updates" element={<UpdatesPage />} />
                {/* 중고폰 보상 — 대표와 직원이 같은 매장 가격표·신청을 함께 관리한다. */}
                <Route path="/used-phone-compensation" element={<UsedPhoneCompensationPage />} />
                {/* 개통 CRM — RBAC crm 그룹. 권한 none이면 진입 차단(현재 모든 역할 write라 실효 없음, 일관성). */}
                <Route element={<RBACRoute pageGroup="crm" />}>
                {/* 개인정보 파기 예정 — 법정 보관기간이 지난 정보가 언제 지워지는지 보고,
                    필요한 자료를 미리 내려받고, 사유를 적어 미룬다. 서버도 requirePermission("crm"). */}
                <Route path="/privacy/destruction" element={<PrivacyDestructionPage />} />
                <Route path="/crm/customers" element={<CrmCustomerListPage />} />
                <Route path="/crm/call-recordings" element={<CallRecordingsPage />} />
                <Route path="/crm/prospects" element={<CrmProspectsPage />} />
                <Route path="/crm/customers/:id" element={<CrmCustomerDetailPage />} />
                <Route path="/crm/applications/:applicationFormId" element={<CrmApplicationDetailPage />} />
                <Route path="/crm/form-templates" element={<FormTemplateCatalogPage />} />
                <Route path="/crm/form-templates/:id" element={<FormTemplateDetailPage />} />
                {/* #226 대리점별 텍스트 신청서 템플릿 — 우측에 치환 미리보기 인라인 */}
                <Route path="/crm/dealer-templates" element={<DealerTemplatesPage />} />
                {/* 고객 입력폼 설정 — 항목 켜기/끄기, 라벨 변경, 추가 항목 */}
                <Route path="/crm/customer-form-settings" element={<CrmCustomerFormSettingsPage />} />
                {/* 파이프라인 단계 관리 — 가운데 단계 추가·이름·색·순서·삭제 (task 312) */}
                <Route path="/crm/pipeline-stages-settings" element={<CrmPipelineStagesSettingsPage />} />
                <Route path="/crm/settlement-config" element={<CrmSettlementConfigPage />} />
                {/* 대리점별 부가서비스 — 가입개통정보에서 ?dealerId= 로 진입 */}
                <Route path="/crm/dealer-addons" element={<CrmDealerAddonsPage />} />
                <Route path="/crm/scheduled-messages" element={<CrmScheduledMessagesPage />} />
                {/* task 405: 고객 페이백 계좌 확인·관리 */}
                <Route path="/crm/payback" element={<CrmPaybackPage />} />
                {/* 스크린챗 #347: 월별 정산 현황판 — 금액 장부라 매장대표만 접근 */}
                <Route element={<StoreManagerRoute />}>
                  <Route path="/crm/settlements" element={<CrmSettlementsPage />} />
                </Route>
                <Route path="/crm/activations-complete" element={<CrmActivationsCompletePage key="activated" view="activated" />} />
                <Route path="/crm/settlements-complete" element={<CrmActivationsCompletePage key="settled" view="settled" />} />
                <Route path="/crm/messages/compose" element={<MessageComposePage />} />
                <Route path="/crm/messages/today" element={<MessageTodayPage />} />
                <Route path="/crm/messages/calendar" element={<MessageCalendarPage />} />
                <Route path="/crm/messages/history" element={<MessageHistoryPage />} />
                <Route path="/crm/messages/rules" element={<MessageRulesPage />} />
                {/* 사용하지 않는 문자 자동화 화면은 제거했다. 옛 북마크는 현행 문자 보내기로 보낸다. */}
                <Route path="/crm/message-automation/*" element={<Navigate to="/crm/messages/compose" replace />} />
                <Route path="/crm/archived-applications" element={<CrmArchivedApplicationsPage />} />
                <Route path="/crm/deleted-applications" element={<CrmDeletedApplicationsPage />} />
                {/* 신청서 데이터 다운로드 — 항목 골라 스프레드시트로 내보내기 */}
                <Route path="/crm/export" element={<CrmExportPage />} />
                {/* 데이터 가져오기 — 직원은 파일 접수, 분석 완료 뒤 확인·수정·반입은 매장대표만. */}
                <Route path="/crm/import" element={<CrmImportPage />} />
                <Route element={<StoreManagerRoute />}>
                  <Route path="/crm/import/:id" element={<CrmImportReviewPage />} />
                </Route>
                {/* 옛 개통이력 — 옛 폼변환기 엑셀 아카이브. 이름·폰번호 검색 전용 분리 뷰. */}
                <Route path="/crm/legacy-activations" element={<CrmLegacyActivationsPage />} />
                {/* 데이터 복원 — 수동 체크포인트 백업(최대 10개). CRM 권한선, 내보내기/가져오기와 같은 자리. */}
                <Route path="/crm/restore" element={<CrmRestorePage />} />
                {/* 신청서 추이 — 일별/주별 생성 추이 대시보드 (설정 및 데이터) */}
                <Route path="/crm/application-trends" element={<CrmApplicationTrendsPage />} />
                </Route>
                {/* 재고관리 — RBAC inventory 그룹 */}
                <Route element={<RBACRoute pageGroup="inventory" />}>
                <Route path="/inventory/devices" element={<InventoryDevicesPage />} />
                {/* 중고폰 관리(티켓 #50) — 단말기관리의 복사본 화면, 장부(stock_type)만 '중고'로 분리.
                    LNB 미노출 — 진입은 단말기관리 제목 옆 버튼. */}
                <Route path="/inventory/used-devices" element={<InventoryUsedDevicesPage />} />
                <Route path="/inventory/holdings" element={<InventoryHoldingsPage />} />
                </Route>
                {/* 카톡 상담 — RBAC kakao_counsel 그룹. 카톡 채널 문의를 우리 상담 링크(/talk/:token)로
                    받아 AI 초안 + 사람 승인으로 응대한다. spec: docs/spec/counsel-chat-spec.md */}
                <Route element={<RBACRoute pageGroup="kakao_counsel" />}>
                <Route path="/kakao-counsel" element={<KakaoCounselPage />} />
                {/* 챗봇 시작하기 — 새로 가입한 매장이 자료를 올려 챗봇을 세팅하는 자리.
                    외부 배포판의 착지점이기도 하다(shared/external-edition.ts).
                    무슨 일이 벌어지는지는 server/src/counsel/onboarding.ts 머리 주석이 진본. */}
                <Route path="/kakao-counsel/start" element={<CounselOnboardingPage />} />
                {/* 과거 상담 내역 업로드 — 카톡 채널 내보내기(csv·zip)를 모아 두는 보관함. 매장 대표 이상. */}
                <Route path="/kakao-counsel/chat-archive" element={<CounselChatArchivePage />} />
                {/* 🗄️ 2026-08-22 — 가르치는 화면을 전부 아카이브했다(훈련장·배운 것·그 옛 주소들).
                    사장님이 화면에서 챗봇을 가르치지 않기로 했고, 대신 매장이 이미 쓰는 안내 템플릿을
                    코드/DB 에 근거로 심었다(server/src/counsel/knowledge-defaults.ts, seed 스크립트).
                    화면 컴포넌트와 서버 API 는 지우지 않았다 — 되살리기는 이 블록을 되돌리면 된다.
                    죽은 주소로 두면 북마크가 빈 화면을 여니 전부 인박스로 넘긴다. */}
                <Route path="/kakao-counsel/training" element={<Navigate to="/kakao-counsel" replace />} />
                <Route path="/kakao-counsel/training/advanced" element={<Navigate to="/kakao-counsel" replace />} />
                <Route path="/kakao-counsel/learned" element={<Navigate to="/kakao-counsel" replace />} />
                <Route path="/kakao-counsel/knowledge" element={<Navigate to="/kakao-counsel" replace />} />
                <Route path="/kakao-counsel/learning" element={<Navigate to="/kakao-counsel" replace />} />
                <Route path="/kakao-counsel/tone" element={<Navigate to="/kakao-counsel" replace />} />
                <Route path="/kakao-counsel/overview" element={<Navigate to="/kakao-counsel" replace />} />
                <Route path="/kakao-counsel/datasources" element={<Navigate to="/kakao-counsel" replace />} />
                <Route path="/kakao-counsel/transcript" element={<Navigate to="/kakao-counsel" replace />} />
                </Route>
                {/* 정책관리 — RBAC policy 그룹. 정책표 파이프라인 화면 (수집·검토·표준화).
                    스펙: docs/spec/policy-pipeline-spec.md — 확정 전 데이터는 합산 미반영(게이트). */}
                <Route element={<RBACRoute pageGroup="policy" />}>
                {/* 관제 화면을 종료해 정책 홈을 다시 자동화 작업대로 연결한다. */}
            <Route path="/policy" element={<Navigate to="/policy/pipeline" replace />} />
                <Route path="/policy/pipeline" element={<PolicyPipelinePage />} />
                {/* 업체·파서 현황 — 업체 등록 · 업체가 안 붙어 어디에도 안 보이던 파일 지정 · 업체별 파서 상태 */}
                <Route path="/policy/vendor-status" element={<PolicyVendorStatusPage />} />
                {/* 업로드 내역 대장 — 업체별로 그동안 올린 액면·구두를 보고 수정·삭제 */}
                <Route path="/policy/uploads" element={<PolicyUploadHistoryPage />} />
                {/* 정책표 원본 보관함 — 업로드한 엑셀 원본을 업체·날짜로 찾아 다시 내려받기 (티켓 #9) */}
                <Route path="/policy/archive" element={<PolicyArchivePage />} />
                {/* 고객 화면에 실제 반영한 시세표의 PNG/XLSX 영구 보관함 (티켓 #626) */}
                <Route path="/policy/quote-exports" element={<PolicyQuoteExportsPage />} />
                <Route path="/policy/calendar" element={<PolicyCalendarPage />} />
                <Route path="/policy/review/face/:id" element={<PolicyFaceReviewPage />} />
                <Route path="/policy/review/verbal/:id" element={<PolicyVerbalReviewPage />} />
                {/* 업체별 구분 그룹 — 업체마다 취급 모델을 구분 그룹으로 묶어 관리 (전 업체 한눈에) */}
                <Route path="/policy/model-groups" element={<PolicyModelGroupsPage />} />
                {/* 별칭 관리 — 도매상 표기 → 표준 단말기·구간 별칭(공통/업체별) 추가·수정·삭제 */}
                <Route path="/policy/aliases" element={<PolicyAliasesPage />} />
                {/* 영역 지정 AI 학습 — AI 가 참고하는 규칙 승인·관리 + 업체별 AI 성적·수정 이력 (spec: policy-region-ai.md) */}
                <Route path="/policy/region-learning" element={<PolicyRegionLearningPage />} />
                {/* 업체 관리 — 정책 업체(별칭·파서) + 개통대리점 연결 */}
                <Route path="/policy/vendors" element={<PolicyVendorsPage />} />
                {/* 리베 조회 — 요금제(구간표 멤버십)→구간→업체별 합산리베. spec: plan-bucket-membership.md */}
                <Route path="/policy/rebate-lookup" element={<PolicyRebateLookupPage />} />
                {/* 전체 리베표 — 통신사×요금제군×업체를 한 매트릭스로 펼친 읽기 전용 뷰 */}
                <Route path="/policy/rebate-matrix" element={<PolicyRebateMatrixPage />} />
                {/* 전체 추출 리베표 — 표준화 이전의 원문 축으로 그려 뽑아낸 행이 하나도 안 빠지는 표.
                    승격 전 리드도 정책 읽기 권한으로 들어온다(화면 범위는 lib/leadDemo.ts 가 좁힘). */}
                <Route path="/policy/rebate-matrix-full" element={<PolicyRebateMatrixFullPage />} />
                {/* 시세표 작성하기 — 통합 정책 기반 고객 안내용 시세표 작성 (현재 빈 페이지) */}
                <Route path="/policy/quote-sheet" element={<PolicyQuoteSheetPage />} />
                {/* 시세표 제작모드 — 매장 직원이 한 장을 함께 쓰는 자유 입력 백지 격자. */}
                <Route path="/policy/quote-sheet-builder" element={<PolicyQuoteSheetBuilderPage />} />
                {/* 내 시세표 틀 — 제작모드 표를 [확정]해 만든 업체 전용 틀. spec: quote-template.md */}
                <Route path="/policy/quote-templates" element={<PolicyQuoteTemplatesPage />} />
                <Route path="/policy/quote-templates/new" element={<PolicyQuoteTemplateNewPage />} />
                <Route path="/policy/quote-templates/:id" element={<PolicyQuoteTemplatePage />} />
                {/* 시세표 안내문구는 별도 페이지를 없애고 시세표 화면(/policy/quote-sheet)의
                    "📢 안내문구"·고객용 링크 모달 안 편집으로 통합(QuoteNoticeModal).
                    옛 북마크·링크가 빈 화면에 안 떨어지게 시세표로 리다이렉트한다. */}
                <Route path="/policy/quote-notice" element={<Navigate to="/policy/quote-sheet" replace />} />
                {/* 요금제 구간표 — 요금제별 구간(군) 소속 전체 열람. spec: plan-bucket-membership.md */}
                <Route path="/policy/plan-groups" element={<PolicyPlanGroupsPage />} />
                {/* 요금제군 한눈에 — 통신사 기준 구간 → 요금제 → 대리점별 군 매트릭스. spec: plan-bucket-membership.md §3.11 */}
                <Route path="/policy/plan-group-matrix" element={<PolicyPlanGroupMatrixPage />} />
                {/* 이상탐지 관측 — 업로드별 시계열 이상 신호. spec: policy-anomaly-detection.md */}
                <Route path="/policy/anomalies" element={<PolicyAnomaliesPage />} />
                <Route path="/policy/conflict-review" element={<PolicyConflictReviewPage />} />
                {/* 공시지원금 변동 추적 — 일/주 단위 시계열. task 335 */}
                <Route path="/policy/subsidy-changes" element={<SubsidyChangesPage />} />
                {/* 모델 관리 — 신모델이 언제·어떤 경로로 들어왔고 확인했는지, 공시·정책표가 붙는지. spec: model-registry.md */}
                <Route path="/policy/models" element={<ModelRegistryPage />} />
                {/* 첫 배포(2026-09-05 오전)는 /admin/models 였다 — 옛 링크를 새 자리로 */}
                <Route path="/admin/models" element={<Navigate to="/policy/models" replace />} />
                </Route>

                {/* 데이터 카탈로그 — 전역 관리자 + 매장대표(owner). owner는 통신사별 이름·판매 범위만
                    수정하고 표준 모델명·제조사는 본사 전용(백엔드 requireCatalogAccess + UI에서 분리). */}
                <Route element={<GlobalAdminRoute allowStoreOwner />}>
                  <Route path="/policy/catalog" element={<PolicyCatalogPage />} />
                </Route>

                {/* 매장대표 전용 — 자기 매장 직원 계정·접근권한 관리.
                    백엔드 /api/v2/store/* (requireStoreManager)가 자기 매장으로 고정한다. */}
                <Route element={<StoreManagerRoute />}>
                  <Route path="/store/staff" element={<StoreStaffPage />} />
                  {/* 매장별 고객 사이트 설정·상품·문의. 매장 식별자는 화면에서
                      받지 않고 백엔드 requireStoreManager가 로그인 계정의 매장으로 고정한다. */}
                  <Route path="/store/storefront" element={<StorefrontManagementPage />} />
                  {/* 본체의 별도 판매 게시글 표는 종료. 옛 링크는 통합 관리의 판매 게시글 탭으로 보낸다. */}
                  <Route path="/store/storefront/posts" element={<Navigate to="/store/storefront?tab=products" replace />} />
                  {/* 하위 판매점 관리 — 상위 판매점(부모 owner)이 자기 밑 직속 자식 매장을 만들고 관리.
                      백엔드 /api/v2/store/child-stores 가 부모/1단계 제약을 강제(프론트는 편의 UI). */}
                  <Route path="/store/child-stores" element={<StoreChildStoresPage />} />
                  {/* 커스텀 폼빌더 관리 — 매장대표가 자기 폼을 만들고 응답을 본다.
                      백엔드 requireStoreManager 와 같은 기준(StoreManagerRoute). responses 는
                      :id 보다 먼저 둬 정적 경로가 우선 매칭되게 한다. */}
                  <Route path="/form-builder" element={<FormBuilderListPage />} />
                  <Route path="/form-builder/responses" element={<FormBuilderResponsesPage />} />
                  <Route path="/form-builder/:id" element={<FormBuilderEditorPage />} />
                </Route>

                {/* 관리자 전용 — AppLayout을 공유해야 LNB 상태(토글 등)가 유지됨 */}
                <Route element={<AdminRoute />}>
                  {/* 정책 파싱이상 콘솔 — 미매핑·파싱이상 신호 집계 (task 316). admin 이상만 접근,
                      owner/tenant 는 정책 메인으로 리다이렉트. 관찰 전용(조치 버튼 없음). */}
                  <Route path="/policy/dev/anomalies" element={<PolicyAnomalyConsolePage />} />
                  {/* 데이터 헬스 — 도매 정책→시세표 데이터의 큰 그림(업체 신선도·신호 히트맵·LLM 브리핑).
                      admin 이상만. 신호는 이상탐지 관측을 종합한 레이어. */}
                  <Route path="/admin/data-health" element={<PolicyDataHealthPage />} />
                  {/* 회원 관리 — 백엔드 admin-users 가 호출자 매장으로 스코프하므로
                      매장대표는 자기 매장 회원만 다룬다(전 매장 관리는 본사 관리자). */}
                  <Route path="/admin/users" element={<AdminUsersPage />} />
                  {/* 사용자 활동 로그 — 누가·언제·어디서·무엇을 했나(시간대 히트맵·인기 페이지·사용자별 타임라인).
                      admin 이상만. 매장 소속 admin(매장대표)은 자기 매장 스코프로 보게 확장 가능(현재 전 매장). */}
                  <Route path="/admin/activity" element={<ActivityDashboardPage />} />
                  {/* 페이지 인벤토리 — LNB 메뉴별 실사용량(안 쓰이는 메뉴 포함)으로 메뉴 정리를 판단.
                      활동 로그가 "사람 기준·기록 있는 것만"이라면 여기는 "메뉴 기준·0건도 보여준다". */}
                  <Route path="/admin/page-inventory" element={<PageInventoryPage />} />
                  {/* 시스템 기능 — 매장대표(매장 소속 admin)는 차단, 전역 관리자만 접근 */}
                  <Route element={<GlobalAdminRoute />}>
                    {/* 매장별 인원·권한 — 매장의 대표·직원 명단 + 역할변경 + 개인 예외.
                        백엔드 /admin/stores 도 requireGlobalAdmin. */}
                    <Route
                      path="/admin/store-members"
                      element={<AdminStoreMembersPage />}
                    />
                    <Route path="/admin/tenants" element={<AdminTenantsPage />} />
                    {/* 크롤링 관리 — 제로노트 일일 크롤링의 실행 이력·변경 내역 */}
                    <Route path="/admin/crawling" element={<AdminCrawlingPage />} />
                    {/* 요금제 알라미 관리 — 공유 Supabase pa_ 테이블 열람·기준일 조정 */}
                    <Route path="/admin/plan-alarm" element={<AdminPlanAlarmPage />} />
                  </Route>
                </Route>

                {/* 전국 판매점 기준데이터 → 연락처 수집 → 오토콜/응답 영업 관리.
                    전역 관리자 + 라인업 매장(admin-demo) 대표만. lineup 은 role=owner 라
                    AdminRoute 로는 막혀, 전용 StoreLeadRoute 로 감싼다(InboundMessagesRoute 와 동일 패턴). */}
                <Route element={<StoreLeadRoute />}>
                  <Route path="/admin/store-leads" element={<StoreLeadManagementPage />} />
                  {/* 사진 판독 결과함 — 판독기가 단가표 사진을 제대로 읽는지 원본과 나란히 보는 본사 실험실 */}
                  <Route path="/admin/photo-read-lab" element={<PhotoReadLabPage />} />
                </Route>

                {/* 수신 메시지 — 포워더가 쌓은 문자·카톡·텔레그램 조회·분류.
                    전역 관리자 + 라인업 매장(admin-demo) 대표만. lineup 은 role=owner 라
                    AdminRoute 로는 막혀, 전용 InboundMessagesRoute 로 감싼다(SalesLeadRoute 와 동일 패턴). */}
                {/* 외부 배포판 기능 스위치 — 고객에게 파는 화면에 무엇을 열어 줄까.
                    고를 수 있는 후보는 코드(shared/external-edition.ts)가 정하고 여기서는 켜고
                    끄기만 한다. 라인업 매장 대표(role=owner)도 봐야 하는데 AdminRoute·
                    GlobalAdminRoute 로는 막혀, 헤더 [내부용|외부용] 탭과 같은 선인 전용
                    EditionAdminRoute 로 감싼다(위 수신 메시지·판매점 리드와 동일 패턴). */}
                <Route element={<EditionAdminRoute />}>
                  <Route
                    path="/admin/external-features"
                    element={<AdminExternalFeaturesPage />}
                  />
                </Route>

                <Route element={<InboundMessagesRoute />}>
                  {/* 관리자용 중간 안내 화면은 종료. 옛 링크는 실제 공개 설치 안내로 보낸다. */}
                  <Route path="/admin/msg-forwarder-guide" element={<Navigate to="/msg-forwarder" replace />} />
                  <Route path="/admin/inbound-messages" element={<InboundMessagesPage />} />
                </Route>
              </Route>
              </Route>
              </Route>
            </Route>
              </Routes>
              </Suspense>
            </ErrorBoundary>
            </NewBuildProvider>
          </ConfirmProvider>
        </ToastProvider>
        </UiZoomProvider>
        </EditionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
